import {
  Box,
  Heading,
  Text,
  Stack,
  TextLink,
  PageBlock,
  Page,
} from 'braid-design-system';
import queryString from 'query-string';
import { Fragment, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Redirect from 'src/components/Redirect/Redirect';
import { SiteWideFooter } from 'src/components/SiteWideFooter';
import { getConfigValues } from 'src/config';
import Routes from 'src/config/routes';
import { useAuth } from 'src/hooks/auth';

interface Auth0AuthenticatedProps {
  children: ReactNode;
}

const Auth0Authenticated = ({ children }: Auth0AuthenticatedProps) => {
  const location = useLocation();
  const { getAuthToken, authLoaded } = useAuth();

  const redirectUrl = queryString.stringifyUrl({
    url: `${getConfigValues().HEADER_SERVER_BASE}${Routes.SIGN_IN_URL}`,
    query: {
      ReturnUrl: location.pathname + location.search,
    },
  });

  if (!authLoaded) {
    return (
      <Page footer={<SiteWideFooter />} footerPosition="belowFold">
        <CustomLoader />
      </Page>
    );
  }

  const authToken = getAuthToken();
  if (authToken) {
    return <Fragment>{children}</Fragment>;
  }

  const debug = getConfigValues().ENVIRONMENT !== 'production';
  if (authLoaded && debug) {
    return (
      <PageBlock width="small">
        <Box paddingY="xxlarge">
          <Stack space="large">
            <Heading level="3">
              Not Logged in: Redirecting to {redirectUrl}
            </Heading>
            <Heading level="4">
              Debug Information (will not show in production)
            </Heading>
            <Text>Auth Loaded: {authLoaded.toString()}</Text>
            <Text>has Token: {Boolean(authToken).toString()}</Text>
            <Text>
              Redirect Url:{' '}
              <TextLink href={redirectUrl}>{redirectUrl}</TextLink>
            </Text>
          </Stack>
        </Box>
      </PageBlock>
    );
  }

  return <Redirect path={redirectUrl} />;
};

export default Auth0Authenticated;
